import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface LockInfo {
    lockedByUserId: string;
    lockedByName: string;
    lockedSince: string;
}

interface UseWizardLockReturn {
    isLocked: boolean;
    isMyLock: boolean;
    lockInfo: LockInfo | null;
    acquiring: boolean;
    acquireLock: () => Promise<boolean>;
    releaseLock: () => Promise<void>;
}

const HEARTBEAT_INTERVAL_MS = 60_000; // 1 min

export function useWizardLock(negociacaoId: number | null, userId: string | undefined): UseWizardLockReturn {
    const [isLocked, setIsLocked] = useState(false);
    const [isMyLock, setIsMyLock] = useState(false);
    const [lockInfo, setLockInfo] = useState<LockInfo | null>(null);
    const [acquiring, setAcquiring] = useState(false);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const lockAcquiredRef = useRef(false);
    // Store negociacaoId and userId in refs for reliable cleanup
    const negociacaoIdRef = useRef(negociacaoId);
    const userIdRef = useRef(userId);

    // Keep refs in sync
    useEffect(() => {
        negociacaoIdRef.current = negociacaoId;
    }, [negociacaoId]);

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    const stopHeartbeat = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    const startHeartbeat = useCallback(() => {
        stopHeartbeat();
        if (!negociacaoId || !userId) return;

        heartbeatRef.current = setInterval(async () => {
            const { data, error } = await supabase.rpc('heartbeat_wizard_lock', {
                p_negociacao_id: negociacaoId,
                p_user_id: userId
            });

            if (error || !data) {
                // Lock was lost — try to re-acquire instead of giving up
                console.warn('Heartbeat failed, attempting to re-acquire lock...');
                const { data: reacquireData } = await supabase.rpc('acquire_wizard_lock', {
                    p_negociacao_id: negociacaoId,
                    p_user_id: userId
                });
                const result = reacquireData?.[0];
                if (!result?.success) {
                    // Another user truly has the lock now
                    stopHeartbeat();
                    setIsMyLock(false);
                    lockAcquiredRef.current = false;
                } else {
                    // Re-adquiriu o lock (ex.: aba acordou após suspensão) — registra nova
                    // sessão para o trabalho não ficar sem rastro em wizard_sessions
                    supabase.rpc('start_wizard_session', {
                        p_negociacao_id: negociacaoId,
                        p_user_id: userId
                    }).then(({ error }) => {
                        if (error) console.error('Error starting wizard session after re-acquire:', error);
                    });
                }
            }
        }, HEARTBEAT_INTERVAL_MS);
    }, [negociacaoId, userId, stopHeartbeat]);

    const acquireLock = useCallback(async (): Promise<boolean> => {
        if (!negociacaoId || !userId) return false;
        setAcquiring(true);

        try {
            const { data, error } = await supabase.rpc('acquire_wizard_lock', {
                p_negociacao_id: negociacaoId,
                p_user_id: userId
            });

            if (error) {
                console.error('Error acquiring lock:', error);
                return false;
            }

            const result = data?.[0];
            if (!result) return false;

            if (result.success) {
                setIsLocked(true);
                setIsMyLock(true);
                setLockInfo({
                    lockedByUserId: result.locked_by_user_id,
                    lockedByName: result.locked_by_name || 'Usuário desconhecido',
                    lockedSince: result.locked_since
                });
                lockAcquiredRef.current = true;
                startHeartbeat();

                // Start wizard session tracking (chronometer)
                supabase.rpc('start_wizard_session', {
                    p_negociacao_id: negociacaoId,
                    p_user_id: userId
                }).then(({ error }) => {
                    if (error) console.error('Error starting wizard session:', error);
                });

                return true;
            } else {
                setIsLocked(true);
                setIsMyLock(false);
                setLockInfo({
                    lockedByUserId: result.locked_by_user_id,
                    lockedByName: result.locked_by_name || 'Usuário desconhecido',
                    lockedSince: result.locked_since
                });
                return false;
            }
        } catch (err) {
            console.error('Unexpected error acquiring lock:', err);
            return false;
        } finally {
            setAcquiring(false);
        }
    }, [negociacaoId, userId, startHeartbeat]);

    const releaseLock = useCallback(async () => {
        stopHeartbeat();
        if (!negociacaoId || !userId) return;

        // Always try to release, don't gate on lockAcquiredRef
        try {
            await supabase.rpc('release_wizard_lock', {
                p_negociacao_id: negociacaoId,
                p_user_id: userId
            });
        } catch (err) {
            console.error('Error releasing lock:', err);
        } finally {
            setIsLocked(false);
            setIsMyLock(false);
            setLockInfo(null);
            lockAcquiredRef.current = false;
        }
    }, [negociacaoId, userId, stopHeartbeat]);

    // Synchronous release via fetch+keepalive (for beforeunload & unmount)
    const releaseViaFetch = useCallback((nId: number, uId: string) => {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/release_wizard_lock`;
        let accessToken = '';
        try {
            const storageKey = `sb-${import.meta.env.VITE_SUPABASE_URL?.split('//')[1]?.split('.')[0]}-auth-token`;
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                accessToken = parsed?.access_token || '';
            }
        } catch { /* ignore */ }

        try {
            fetch(url, {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    p_negociacao_id: nId,
                    p_user_id: uId
                })
            }).catch(() => {});
        } catch { /* ignore - best effort */ }
    }, []);

    // Release lock on unmount or page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            const nId = negociacaoIdRef.current;
            const uId = userIdRef.current;
            if (lockAcquiredRef.current && nId && uId) {
                releaseViaFetch(nId, uId);
            }
        };

        // Also listen for visibilitychange as a safety net
        const handleVisibilityChange = () => {
            // If page is hidden (tab switch, minimize), do nothing - only on unload
            // But if the document is being discarded (page navigation), try release
            if (document.visibilityState === 'hidden') {
                const nId = negociacaoIdRef.current;
                const uId = userIdRef.current;
                // Only release if we're actually navigating away from the wizard
                // The component unmount handler is more reliable for SPA navigation
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            stopHeartbeat();

            // Release lock on component unmount using refs (stable values)
            const nId = negociacaoIdRef.current;
            const uId = userIdRef.current;
            if (lockAcquiredRef.current && nId && uId) {
                // Use both: async RPC call AND fetch+keepalive as fallback
                supabase.rpc('release_wizard_lock', {
                    p_negociacao_id: nId,
                    p_user_id: uId
                }).then(({ error }) => {
                    if (error) console.error('Error releasing lock on unmount:', error);
                });

                // Fetch as backup in case the RPC promise gets cancelled
                releaseViaFetch(nId, uId);
            }
            lockAcquiredRef.current = false;
        };
    }, [stopHeartbeat, releaseViaFetch]);

    return {
        isLocked,
        isMyLock,
        lockInfo,
        acquiring,
        acquireLock,
        releaseLock
    };
}

/**
 * Checa se um lock existe para uma negociação (sem tentar adquirir).
 * Usado para mostrar indicadores visuais na listagem.
 */
export async function checkWizardLock(negociacaoId: number): Promise<{
    isLocked: boolean;
    lockedByName?: string;
    lockedSince?: string;
} | null> {
    const { data, error } = await supabase
        .from('wizard_locks')
        .select('locked_by, locked_at, last_heartbeat')
        .eq('negociacao_id', negociacaoId)
        .maybeSingle();

    if (error || !data) return { isLocked: false };

    // Check if expired (>15 min)
    const lastHeartbeat = new Date(data.last_heartbeat);
    const now = new Date();
    const diffMs = now.getTime() - lastHeartbeat.getTime();
    if (diffMs > 15 * 60 * 1000) {
        return { isLocked: false };
    }

    // Fetch profile name
    const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', data.locked_by)
        .single();

    return {
        isLocked: true,
        lockedByName: profile?.full_name || 'Usuário desconhecido',
        lockedSince: data.locked_at
    };
}
